import { useEffect, useState } from 'react';
import axios from 'axios';
import MenuHeader from '@/components/MenuHeader';
import Modal from '@/components/Modal';
import CategorySection from '@/components/CategorySection';
import ProductMenu from '@/components/ProductMenu';
import { toast } from 'react-toastify';
import { AES, enc } from 'crypto-js';
import BottomNavbar from '@/components/BottomNavbar';

const secretKey = import.meta.env.VITE_AES_ENCRYPTION_KEY;

const MenuPage = () => {
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [formulas, setFormulas] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const [selectedFormulaId, setSelectedFormulaId] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedRestaurantId, setSelectedRestaurantId] = useState();

    const fetchCategoriesAndFormulas = async () => {
        try {
            const [categoriesResponse, formulasResponse] = await Promise.all([
                axios.get(`${import.meta.env.VITE_API_URL}/api/settings/categories/list`),
                axios.get(`${import.meta.env.VITE_API_URL}/api/restaurants/${selectedRestaurantId}/menu/list`)
            ]);

            if (categoriesResponse.status === 200 && formulasResponse.status === 200) {
                const categories = categoriesResponse.data;
                const formulas = formulasResponse.data;

                const filteredCategories = await Promise.all(categories.map(async (category) => {
                    if (category.id === '1MyBSfCxmY2dHIhFHYF8') {
                        return null; // Ne pas inclure cette catégorie
                    }
                    const hasProducts = await checkCategoryHasProducts(category.id);
                    const hasFormulas = formulas.some(formula => formula.category_id === category.id);
                    if (hasProducts || hasFormulas) {
                        return category;
                    } else {
                        return null;
                    }
                }));

                setCategories(filteredCategories.filter(category => category !== null));
                setFormulas(formulas);
            } else {
                console.error('Failed to fetch categories or formulas');
            }
        } catch (error) {
            console.error('Error fetching categories or formulas:', error);
        }
    };

    const checkCategoryHasProducts = async (categoryId) => {
        try {
            const response = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/restaurants/${selectedRestaurantId}/categories/${categoryId}/products`
            );
            return response.status === 200 && response.data.length > 0;
        } catch (error) {
            if (error.response && error.response.status === 404) {
                return false;
            }
            console.error(`Error checking products for category ${categoryId}:`, error);
            return false;
        }
    };

    const fetchDefaultRestaurantId = async () => {
        try {
            const response = await axios.get('/api/restaurants/get-first-priority-restaurant');

            if (response.status === 200) {
                setSelectedRestaurantId(response.data.restaurant_id);
            }
        } catch (error) {
            console.info(error);

            toast.error('Une erreur est survenue', {
                position: 'top-center',
                autoClose: 1000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: true,
                theme: 'colored',
            });
        }
    };

    const fetchProductsByCategory = async (categoryId) => {
        try {
            const response = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/restaurants/${selectedRestaurantId}/categories/${categoryId}/products`
            );
            if (response.status === 200) {
                setProducts(response.data);
            } else {
                console.error('Failed to fetch products');
            }
        } catch (error) {
            if (error.response && error.response.status === 404) {
                console.log(`No products found for category ${categoryId}`);
            } else {
                console.error(`Error fetching products for category ${categoryId}:`, error);
            }
        }
    };

    useEffect(() => {
        fetchDefaultRestaurantId();
        setSelectedFormulaId('formulas');

        const storedHashedId = localStorage.getItem('restaurant_id_ref');

        if (storedHashedId) {
            const originalStoredId = AES.decrypt(storedHashedId, secretKey).toString(enc.Utf8);
            setSelectedRestaurantId(originalStoredId);
        }
    }, []);

    useEffect(() => {
        if (selectedRestaurantId) {
            fetchCategoriesAndFormulas();
        }
    }, [selectedRestaurantId]);

    useEffect(() => {
        if (selectedCategoryId && selectedRestaurantId) {
            fetchProductsByCategory(selectedCategoryId);
        }
    }, [selectedCategoryId, selectedRestaurantId]);

    const handleCategoryClick = (categoryId) => {
        setSelectedCategoryId(categoryId);
        setSelectedFormulaId('');
    };

    const handleFormulaClick = () => {
        setSelectedCategoryId('');
        setProducts([]);
        setSelectedFormulaId('formulas');
    };

    const handleProductClick = (product) => {
        setSelectedProduct(product);
        setShowModal(true);
    };

    const handleSelectRestaurant = (restaurantId) => {
        localStorage.setItem('restaurant_id_ref', restaurantId);
        setSelectedRestaurantId(restaurantId);
    };

    return (
        <>
            <div className="h-screen w-screen flex items-center justify-center overflow-y-auto">
                <div className="w-full h-full flex flex-col items-center">
                    <MenuHeader onSelectRestaurant={handleSelectRestaurant} selectedRestaurantId={selectedRestaurantId} />
                    <CategorySection
                        categories={categories}
                        formulas={formulas}
                        onCategoryClick={handleCategoryClick}
                        onFormulaClick={handleFormulaClick}
                    />
                    <div className='p-2'>
                        <ProductMenu
                            products={products}
                            formulas={selectedFormulaId === 'formulas' ? formulas : []}
                            onProductClick={handleProductClick}
                        />
                    </div>
                    {showModal && selectedProduct && (
                        <Modal setShowModal={setShowModal} title={selectedProduct.name} type={"info"} showFooterButtons={false}>
                            <img src={selectedProduct.picture} alt={selectedProduct.name} className="w-full h-48 object-cover rounded-md mb-4" />
                            <p className="text-gray-600 mb-4 text-center">Prix: {selectedProduct.price} €</p>
                            <p dangerouslySetInnerHTML={{ __html: selectedProduct.description }} />
                        </Modal>
                    )}
                </div>
            </div>

            <BottomNavbar />
        </>
    );
};

export default MenuPage;